import { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Card } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import MediaPicker, { mediaSrc } from "../../../components/MediaPicker";
import { ImageIcon, Plus, Trash2, ArrowUp, ArrowDown, X } from "lucide-react";
import { FONTS, PAGES, rid } from "./blocks";

export function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 mt-1">
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value || "") ? value : "#ffffff"} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 rounded border border-slate-200 p-0.5 bg-white" data-testid={testid} />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Varsayılan" className="font-mono text-xs" />
        {value ? <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} title="Sıfırla"><X className="h-4 w-4" /></Button> : null}
      </div>
    </div>
  );
}

export function ImageField({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {label && <Label className="text-xs">{label}</Label>}
      <div className="flex gap-2 mt-1 items-center">
        <button type="button" onClick={() => setOpen(true)} data-testid={testid}
          className="h-14 w-20 shrink-0 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden hover:border-blue-400">
          {value ? <img src={mediaSrc(value, true)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-slate-400" />}
        </button>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Görsel seçin veya adres yapıştırın" className="text-xs" />
        {value ? <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")}><X className="h-4 w-4" /></Button> : null}
      </div>
      <MediaPicker open={open} onOpenChange={setOpen} onPick={(m) => onChange(m.path)} />
    </div>
  );
}

const Sec = ({ title, hint, children }) => (
  <Card className="p-5 border border-slate-200 shadow-none space-y-4">
    <div><div className="font-semibold text-slate-900">{title}</div>{hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}</div>
    {children}
  </Card>
);
const F = ({ label, children }) => <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;

export function ThemePanel({ st, patch }) {
  const th = st.theme || {};
  const set = (k, v) => patch("theme", { ...th, [k]: v });
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="studio-theme">
      <Sec title="Site renkleri" hint="Tüm sayfalarda buton, bağlantı, başlık ve kart renklerini tek yerden değiştirir.">
        <div className="grid grid-cols-2 gap-3">
          <ColorField label="Ana renk (butonlar)" value={th.primary} onChange={(v) => set("primary", v)} testid="theme-primary" />
          <ColorField label="Ana renk (koyu)" value={th.primary_dark} onChange={(v) => set("primary_dark", v)} />
          <ColorField label="Başlık / lacivert" value={th.navy} onChange={(v) => set("navy", v)} />
          <ColorField label="Soluk metin" value={th.muted} onChange={(v) => set("muted", v)} />
          <ColorField label="Kenarlık" value={th.border} onChange={(v) => set("border", v)} />
          <ColorField label="Sayfa zemini" value={th.bg} onChange={(v) => set("bg", v)} />
          <ColorField label="Kart zemini" value={th.card} onChange={(v) => set("card", v)} />
          <ColorField label="Marka yazı rengi" value={th.brand} onChange={(v) => set("brand", v)} />
        </div>
      </Sec>
      <Sec title="Yazı tipi ve köşeler">
        <F label="Yazı tipi">
          <Select value={th.font || "_"} onValueChange={(v) => set("font", v === "_" ? "" : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{FONTS.map(([v, l]) => <SelectItem key={v || "_"} value={v || "_"}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label={`Kart köşe yuvarlaklığı: ${th.radius === undefined || th.radius === "" ? "varsayılan" : th.radius + "px"}`}>
          <input type="range" min="0" max="32" value={th.radius || 0} onChange={(e) => set("radius", e.target.value)} className="w-full" />
        </F>
        <Button variant="outline" size="sm" onClick={() => patch("theme", {})}>Tüm tema ayarlarını sıfırla</Button>
      </Sec>
    </div>
  );
}

export function GlobalPanel({ st, patch }) {
  const g = st.global || {}, f = st.footer || {}, soc = g.social || {};
  const setG = (k, v) => patch("global", { ...g, [k]: v });
  const setF = (k, v) => patch("footer", { ...f, [k]: v });
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="studio-global">
      <Sec title="İletişim bilgileri" hint="Sitedeki tüm telefon, e-posta ve WhatsApp bağlantıları otomatik güncellenir.">
        <div className="grid grid-cols-2 gap-3">
          <F label="Telefon (arama için)"><Input value={g.phone || ""} onChange={(e) => setG("phone", e.target.value)} placeholder="+905001112233" data-testid="g-phone" /></F>
          <F label="Telefon (görünen)"><Input value={g.phone_display || ""} onChange={(e) => setG("phone_display", e.target.value)} placeholder="0 500 111 22 33" /></F>
          <F label="E-posta"><Input value={g.email || ""} onChange={(e) => setG("email", e.target.value)} /></F>
          <F label="WhatsApp numarası"><Input value={g.whatsapp || ""} onChange={(e) => setG("whatsapp", e.target.value)} placeholder="905001112233" /></F>
        </div>
        <F label="Adres"><Textarea rows={2} value={g.address || ""} onChange={(e) => setG("address", e.target.value)} /></F>
        <div className="grid grid-cols-2 gap-3">
          {["facebook", "instagram", "youtube", "twitter"].map((k) => (
            <F key={k} label={k[0].toUpperCase() + k.slice(1)}><Input value={soc[k] || ""} onChange={(e) => setG("social", { ...soc, [k]: e.target.value })} placeholder="https://" /></F>
          ))}
        </div>
      </Sec>
      <div className="space-y-4">
        <Sec title="Logo ve ikon">
          <ImageField label="Site logosu" value={g.logo_url} onChange={(v) => setG("logo_url", v)} testid="g-logo" />
          <ImageField label="Favicon" value={g.favicon_url} onChange={(v) => setG("favicon_url", v)} />
        </Sec>
        <Sec title="Alt bilgi (footer)" hint="Renkler ve telif/ek not metni. Footer içindeki bağlantı ve kolonlar Görsel Editör'den düzenlenir.">
          <div className="grid grid-cols-3 gap-3">
            <ColorField label="Zemin" value={f.bg} onChange={(v) => setF("bg", v)} testid="f-bg" />
            <ColorField label="Yazı" value={f.text} onChange={(v) => setF("text", v)} />
            <ColorField label="Başlık" value={f.title} onChange={(v) => setF("title", v)} />
          </div>
          <F label="Telif metni"><Input value={f.copyright || ""} onChange={(e) => setF("copyright", e.target.value)} placeholder="© 2026 ServisPlus. Tüm hakları saklıdır." /></F>
          <F label="Ek not (footer altı)"><Textarea rows={2} value={f.note || ""} onChange={(e) => setF("note", e.target.value)} /></F>
        </Sec>
      </div>
    </div>
  );
}

export function HeroPanel({ st, patch }) {
  const h = st.hero || {};
  const set = (k, v) => patch("hero", { ...h, [k]: v });
  const slides = h.slides || [];
  const upd = (i, k, v) => set("slides", slides.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const mv = (i, d) => { const a = [...slides]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set("slides", a); };
  return (
    <div className="space-y-4" data-testid="studio-hero">
      <Sec title="Ana sayfa vitrini" hint="Açıkken ana sayfadaki üst banner alanı bu görsel/slider ile değişir. Kapalıyken sitenin kendi bannerı görünür.">
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={!!h.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="hero-enabled" /> Vitrini kullan</label>
          <div className="flex gap-2">
            {[["single", "Tek resim"], ["slider", "Slayt (kaydırmalı)"]].map(([v, l]) => (
              <Button key={v} type="button" size="sm" variant={h.mode === v ? "default" : "outline"} className={h.mode === v ? "bg-blue-600 hover:bg-blue-700" : ""} onClick={() => set("mode", v)} data-testid={`hero-mode-${v}`}>{l}</Button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <F label="Yükseklik (px)"><Input type="number" value={h.height || 440} onChange={(e) => set("height", +e.target.value)} /></F>
          <F label="Karartma (0-1)"><Input type="number" step="0.05" min="0" max="1" value={h.overlay ?? 0.35} onChange={(e) => set("overlay", +e.target.value)} /></F>
          <F label="Geçiş süresi (sn)"><Input type="number" value={h.interval || 5} onChange={(e) => set("interval", +e.target.value)} /></F>
          <label className="flex items-end gap-2 text-sm pb-2"><Switch checked={h.autoplay !== false} onCheckedChange={(v) => set("autoplay", v)} /> Otomatik geçiş</label>
        </div>
      </Sec>
      {slides.map((s, i) => (
        <Card key={s.id || i} className="p-4 border border-slate-200 shadow-none" data-testid={`hero-slide-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-sm">{h.mode === "single" && i > 0 ? `Slayt ${i + 1} (tek resim modunda gösterilmez)` : `Slayt ${i + 1}`}</div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => mv(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => mv(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => set("slides", slides.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <ImageField label="Masaüstü görseli" value={s.image} onChange={(v) => upd(i, "image", v)} testid={`hero-img-${i}`} />
            <ImageField label="Mobil görseli (isteğe bağlı)" value={s.mobile_image} onChange={(v) => upd(i, "mobile_image", v)} />
            <F label="Başlık"><Input value={s.title || ""} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`hero-title-${i}`} /></F>
            <F label="Alt metin (SEO)"><Input value={s.alt || ""} onChange={(e) => upd(i, "alt", e.target.value)} /></F>
            <div className="md:col-span-2"><F label="Açıklama"><Textarea rows={2} value={s.text || ""} onChange={(e) => upd(i, "text", e.target.value)} /></F></div>
            <F label="Buton yazısı"><Input value={s.cta_text || ""} onChange={(e) => upd(i, "cta_text", e.target.value)} /></F>
            <F label="Buton bağlantısı"><Input value={s.cta_url || ""} onChange={(e) => upd(i, "cta_url", e.target.value)} placeholder="/iletisim/" /></F>
          </div>
        </Card>
      ))}
      <Button variant="outline" onClick={() => set("slides", [...slides, { id: rid(), image: "", title: "", text: "", cta_text: "", cta_url: "" }])} data-testid="hero-add-slide"><Plus className="h-4 w-4 mr-2" />Slayt ekle</Button>
    </div>
  );
}

const loc = (iso) => { if (!iso) return ""; const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

export function BannerCodePanel({ st, patch }) {
  const b = st.banner || {}, c = st.custom || {};
  const setB = (k, v) => patch("banner", { ...b, [k]: v });
  const setC = (k, v) => patch("custom", { ...c, [k]: v });
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="studio-banner">
      <Sec title="Üst duyuru şeridi" hint="Tüm sayfaların en üstünde çıkan kampanya/duyuru bandı.">
        <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={!!b.enabled} onCheckedChange={(v) => setB("enabled", v)} data-testid="banner-enabled" /> Şeridi göster</label>
        <F label="Metin"><Input value={b.text || ""} onChange={(e) => setB("text", e.target.value)} data-testid="banner-text" /></F>
        <F label="Bağlantı (isteğe bağlı)"><Input value={b.link || ""} onChange={(e) => setB("link", e.target.value)} /></F>
        <div className="grid grid-cols-2 gap-3"><ColorField label="Zemin" value={b.bg} onChange={(v) => setB("bg", v)} /><ColorField label="Yazı" value={b.color} onChange={(v) => setB("color", v)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Başlangıç (boşsa hemen)"><Input type="datetime-local" value={loc(b.start_at)} onChange={(e) => setB("start_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="banner-start" /></F>
          <F label="Bitiş (boşsa süresiz)"><Input type="datetime-local" value={loc(b.end_at)} onChange={(e) => setB("end_at", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="banner-end" /></F>
        </div>
        <p className="text-[11px] text-slate-500">Tarih aralığı yayından sonra da geçerlidir: şerit, belirlediğiniz tarihte kendiliğinden açılır/kapanır (tekrar yayın gerekmez).</p>
      </Sec>
      <Sec title="Gelişmiş: özel kod" hint="Dikkatli kullanın. Hatalı kod sayfayı bozabilir; sorun olursa Sürümler'den eski sürüme dönebilirsiniz.">
        <F label="Özel CSS (tüm sayfalar)"><Textarea rows={5} className="font-mono text-xs" value={c.css || ""} onChange={(e) => setC("css", e.target.value)} /></F>
        <F label="Sayfa sonuna HTML/script (ör. analitik)"><Textarea rows={4} className="font-mono text-xs" value={c.body_end_html || ""} onChange={(e) => setC("body_end_html", e.target.value)} /></F>
      </Sec>
    </div>
  );
}

export function SeoPanel({ st, setOps }) {
  const [path, setPath] = useState("/");
  const ops = st.ops || [];
  const cur = ops.find((o) => o.type === "meta" && o.scope === "page" && o.path === path) || {};
  const set = (k, v) => {
    const rest = ops.filter((o) => o !== cur && !(o.type === "meta" && o.scope === "page" && o.path === path));
    const next = { ...cur, id: cur.id || rid(), type: "meta", scope: "page", path, [k]: v };
    const empty = !next.title && !next.description && !next.og_image && !next.noindex;
    setOps(empty ? rest : [...rest, next]);
  };
  const metas = ops.filter((o) => o.type === "meta");
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="studio-seo">
      <Sec title="Sayfa seçin">
        <Select value={PAGES.some((p) => p[0] === path) ? path : "_c"} onValueChange={(v) => v !== "_c" && setPath(v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{PAGES.map(([p, l]) => <SelectItem key={p} value={p}>{l} — {p}</SelectItem>)}<SelectItem value="_c">Özel adres…</SelectItem></SelectContent>
        </Select>
        <Input value={path} onChange={(e) => setPath(e.target.value)} className="font-mono text-xs" />
        <div className="text-xs text-slate-500">Özelleştirilmiş sayfa: {metas.length}</div>
      </Sec>
      <div className="lg:col-span-2">
        <Sec title="Arama motoru ve paylaşım ayarları" hint="Not: Başlık/açıklama sayfa yüklendikten sonra uygulanır; Google'ın JavaScript'i çalıştırması ile okunur. Kritik SEO için dosyadaki özgün başlık da korunmalıdır.">
          <F label={`Sayfa başlığı (${(cur.title || "").length}/60)`}><Input value={cur.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="seo-title" /></F>
          <F label={`Açıklama (${(cur.description || "").length}/160)`}><Textarea rows={3} value={cur.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="seo-desc" /></F>
          <ImageField label="Paylaşım görseli (og:image)" value={cur.og_image} onChange={(v) => set("og_image", v)} />
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!cur.noindex} onCheckedChange={(v) => set("noindex", v)} /> Arama motorlarından gizle (noindex)</label>
        </Sec>
      </div>
    </div>
  );
}
